import React, { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { toast } from 'react-toastify'

import AddIcon from '../../../assets/img/ico-add.svg?react'
import {
    selectNotificationEvents,
    selectUpdateNotification,
    setUpdateNotification,
} from '../../../features/notifications/notificationsSlice'
import useModal from '../../../hooks/useModal'
import { useCreateNotificationEventMutation } from '../../../services/notifications/notifications'
import type {
    NotificationType,
    ScheduleType,
    TargetType,
} from '../../../types/notifications'
import Input, { InputStyles } from '../../common/Input'
import Modal from '../../common/Modal'
import SelectBox from '../../common/SelectBox'

import AddEventModal from './AddEventModal'

const scheduleOptions: { label: string; value: ScheduleType }[] = [
    { label: 'At the time of the event', value: 'immediate' },
    { label: 'Delayed', value: 'delayed' },
    { label: 'Specific Time', value: 'specific_time' },
]

const typeOptions: { label: string; value: NotificationType }[] = [
    { label: 'Email', value: 'email' },
    { label: 'SMS', value: 'sms' },
    { label: 'Notifications', value: 'notification' },
]

export const GeneralSettingsTab: React.FC = () => {
    const dispatch = useDispatch()
    const formState = useSelector(selectUpdateNotification)
    const events = useSelector(selectNotificationEvents)
    const { isOpen, openModal, closeModal } = useModal()
    const [delay, setDelay] = useState<number>()
    const [scheduledAt, setScheduledAt] = useState('')

    const handleSelectChange = (key: string, value: any) => {
        dispatch(setUpdateNotification({ [key]: value || '' }))
    }

    const handleCheckboxChange = (value: NotificationType) => {
        const currentMethods = formState.type || []
        const updatedMethods = currentMethods.includes(value)
            ? currentMethods.filter((method: string) => method !== value)
            : [...currentMethods, value]

        dispatch(setUpdateNotification({ type: updatedMethods }))
    }

    const handleDelayedInput = (value: string) => {
        setDelay(+value)
        dispatch(setUpdateNotification({ delay: +value }))
    }

    const handleSpecificTimeInput = (value: string) => {
        setScheduledAt(value)
        dispatch(setUpdateNotification({ scheduledAt: value }))
    }

    return (
        <form className="custom-form" method="POST">
            <div className="custom-form-body">
                <div className="row gutters-16">
                    <div className="col-12">
                        <div className="flex flex-group--xs items-end w-full">
                            <SelectBox
                                label="Select the event"
                                wrapperClassName="select-secondary form-group--row input--lg w-full"
                                options={events.map(
                                    (event: { id: string; name: string }) => ({
                                        label: event.name,
                                        value: event.id,
                                    }),
                                )}
                                placeholder="Select the event"
                                value={formState?.eventId || ''}
                                onChange={(option) =>
                                    handleSelectChange('eventId', option?.value)
                                }
                            />
                            <button
                                onClick={() => openModal('add-event')}
                                className="btn btn--primary-gradient btn--lg fullRadius whitespace-nowrap h-fit"
                                type="button"
                            >
                                <AddIcon />
                                <span className="text--xs">Add Event</span>
                            </button>
                        </div>
                    </div>
                    <div className="col-12">
                        <SelectBox
                            label="Schedule"
                            wrapperClassName="select-secondary form-group--row input--lg"
                            options={scheduleOptions}
                            placeholder="Select the schedule"
                            value={(formState?.schedule as ScheduleType) || ''}
                            onChange={(option) =>
                                handleSelectChange(
                                    'schedule',
                                    option?.value as ScheduleType,
                                )
                            }
                        />
                    </div>
                    {formState.schedule === 'delayed' && (
                        <div className="col-12">
                            <Input
                                inputStyle={InputStyles.outline}
                                label="Delay (seconds)"
                                placeholder="Enter delay in seconds"
                                type="number"
                                value={delay}
                                onChange={(e) =>
                                    handleDelayedInput(e.target.value)
                                }
                            />
                        </div>
                    )}
                    {formState.schedule === 'specific_time' && (
                        <div className="col-12">
                            <Input
                                inputStyle={InputStyles.outline}
                                label="Specific Time"
                                placeholder="Select date and time"
                                type="datetime-local"
                                value={scheduledAt}
                                onChange={(e) =>
                                    handleSpecificTimeInput(e.target.value)
                                }
                            />
                        </div>
                    )}
                    <div className="col-12 mt-5 mb-2">
                        <div className="form-group--row input--xl items-start">
                            <ul className="labels-list">
                                {typeOptions.map(({ label, value }) => (
                                    <li key={value} className="label-item">
                                        <label className="custom-checkbox">
                                            <input
                                                type="checkbox"
                                                name="notification-method"
                                                checked={
                                                    formState?.type?.includes(
                                                        value,
                                                    ) || false
                                                }
                                                onChange={() =>
                                                    handleCheckboxChange(value)
                                                }
                                                className="custom-checkbox__input"
                                            />
                                            <span className="custom-checkbox__input-fake">
                                                <span className="icon-ico-check"></span>
                                            </span>
                                            <span className="custom-checkbox__label">
                                                {label}
                                            </span>
                                        </label>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </div>
            </div>

            <AddEventModal
                isOpen={isOpen('add-event')}
                onClose={() => closeModal('add-event')}
                events={events}
            />
        </form>
    )
}
